/* eslint-disable */
import { CircularProgress } from "@mui/material";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import { SnackbarOrigin } from "@mui/material/Snackbar";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { styled } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import * as React from "react";
import { useRef } from "react";
import { connect, useSelector, type ConnectedProps } from "react-redux";
import AuthError from "../../../../components/ErrorComponents/AuthError";
import GenericModal from "../../../../components/Modal";
import Pagination from "../../../../components/TablePagination";
import LanguageStrings from "../../../../i18n/locales";
import { type RootState } from "../../../../store";
import { fetchSiteList, siteDeleteCampaign } from "../../redux/actionCreators";
import { SiteListState } from "../../redux/siteListSlice";
import { APIConfiguration } from '../../../../utils/apiConfiguration'
import { useNavigate } from "react-router-dom";
import ViewSite from '../ViewSite/ViewSite'
import { getOperationPermissions } from '../../../dashboard/redux/actionCreator';
import { type OperationPermissionState } from '../../../dashboard/redux/operationPermissionSlice'
import { useAuth } from 'react-oidc-context'
import useRefreshHamburgNotifier from '../../../../customHooks/useRefreshHamburgNotifier'

const DeviceManagementStrings = LanguageStrings().SiteManagementCollectionStrings.TableComponent;
const httpStatus = APIConfiguration

export interface State extends SnackbarOrigin {
  open: boolean;
}

interface Data {
  site_name: string;
  description: string;
}
export type Order = "asc" | "desc";

interface HeadCell {
  disablePadding: boolean;
  id: any;
  label: string;
  numeric: boolean;
  sortable: boolean;
}
const headCells: readonly HeadCell[] = [
  {
    id: "siteName",
    numeric: false,
    disablePadding: false,
    label: DeviceManagementStrings.SiteName,
    sortable: true,
  },
  {
    id: "description",
    numeric: false,
    disablePadding: false,
    label: DeviceManagementStrings.Description,
    sortable: false,
  },
  {
    id: "action",
    numeric: false,
    disablePadding: false,
    label: DeviceManagementStrings.Action,
    sortable: false,
  },
];

function EnhancedTableHead(props: any): any {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler =
    (newOrderBy: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, newOrderBy);
    };
  // istanbul ignore else
  return (
    <TableHead data-testid="table-header">
      <TableRow>
        {headCells.map((headCell: any) => (
          <StyledTableCell
            key={headCell.id}
            align={
              /* istanbul ignore next */
              headCell.numeric ? "right" : "left"
            }
            padding={
              /* istanbul ignore next */
              headCell.disablePadding ? "none" : "normal"
            }
            sortDirection={
              /* istanbul ignore next */
              orderBy === headCell.id ? order : false
            }
          >
            <TableSortLabel
              sx={{
                "& .MuiTableSortLabel-icon": { 
                  opacity: 1,
                  marginLeft: '215px'
                } 
              }}
              hideSortIcon={!headCell.sortable}
              direction={
                /* istanbul ignore next */
                orderBy === headCell.id ? order : "desc"
              }
              onClick={headCell.sortable ? createSortHandler(headCell.id) : undefined}
              aria-label={headCell.label}
              data-testid="test-sort"
            >
              {headCell.label}
              {
                /* istanbul ignore next */
                orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc" ? "sorted descending" : "sorted ascending"}
                  </Box>
                ) : null
              }
            </TableSortLabel>
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: "#F0F0F0",
    fontWeight: "700",
    fontSize: 12,
    border: "2px solid #202020",
    padding: "5px 16px",
    fontFamily: "Honeywell Sans Web",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: "#d0d0d0",
    paddingLeft: "17px",
    borderBottom: "1px solid #202020",
    borderRight: 0,
    fontFamily: "Honeywell Sans Web",
  },
}));

export interface Sort {
  order: Order;
  orderBy: string;
}

function SiteList(props: AllProps) {
  const navigate = useNavigate()
  const [confirmDeletePopup, setConfirmDeletePopup] = React.useState(false);
  const [selectedSiteId, setSelectedSiteId] = React.useState("");
  const [selectedSiteName, setSelectedSiteName] = React.useState("");
  const [showDeleteStatus, setShowDeleteStatus] = React.useState(false);
  const [open, setOpen] = React.useState<any>('')
  const operationPermissions: OperationPermissionState = useSelector((state: RootState) => state.operationPermissions)
  const auth = useAuth()
  const { refreshHamburgAction } = useRefreshHamburgNotifier()

  React.useEffect(() => {
    (!operationPermissions.updated && props.getOperationPermissions(auth.user?.profile.sub as string))
  }, [])

  const siteDeleteStatus = useSelector(
    (state: RootState) => state.siteDeleteState
  );
  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef);
  // istanbul ignore next
  function useOutsideAlerter(ref: any) {
  }

  React.useEffect(() => {
    /* istanbul ignore next */
    if (props.searchVal === "") {
      fetchAllAssetList();
    } else if (props.searchVal !== "") {
      props.performAction();
    }
  }, [props.page, props.sort]);

  const fetchAllAssetList = () => {
    // istanbul ignore else
    if (props.page >= 1) {
      props.fetchSiteList(props.page, props.rowsPerPage, props.searchVal, props.sort.order, props.sort.orderBy);
    }
  };

  const handleRequestSort = ( event: React.MouseEvent<unknown>, newOrderBy: keyof Data ) => {
    /* istanbul ignore next */
    const isAsc = props.sort.orderBy === newOrderBy && props.sort.order === "asc";
    /* istanbul ignore next */
    const toggledOrder = isAsc ? "desc" : "asc";
    /* istanbul ignore next */
    props.setSort({ order: toggledOrder, orderBy: newOrderBy });
  };

  const handleDeviceDetailClick = (assetId: string, assetName: string) => {
    navigate(`/thermal-asset-list/${assetId}`)
  };

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "& th": {
      border: "1px solid #202020",
    },
  }));

  const handleDeleteCampaignWithFailure = (): JSX.Element => {
    /* istanbul ignore next */
    if (siteDeleteStatus.httpStatus === httpStatus.SUCCESS_HTTP_STATUS) {
      return (
        <GenericModal
          buttonTypeNegative="hollow"
          title={DeviceManagementStrings.successfullyDeleted}
          LabelPositive="Okay"
          onClickPositive={() => {
            refreshHamburgAction('Site', 'Delete'), setShowDeleteStatus(false), fetchAllAssetList()
          }}
          onClose={() => {
            refreshHamburgAction('Site', 'Delete'), setShowDeleteStatus(false), fetchAllAssetList()
          }}
        >
          <p style={{ paddingTop: "1.2em" }}>
            {" "}
            {DeviceManagementStrings.FullMsgSiteDeletedSuccessfully}{" "}
          </p>
        </GenericModal>
      );
    } else if (siteDeleteStatus.isLoading) {
      return (
        <GenericModal title={"Loading..."}>
          <div className="CircularprogressAmi">
            <CircularProgress />
          </div>
        </GenericModal>
      );
    } else if (siteDeleteStatus.httpStatus === httpStatus.BAD_REQUEST) {
      return (
        <GenericModal
          buttonTypeNegative="hollow"
          title={"Failed"}
          LabelPositive="Okay"
          onClickPositive={() => {
            setShowDeleteStatus(false);
          }}
          onClose={() => {
            setShowDeleteStatus(false);
          }}
        >
          <p style={{ paddingTop: "1.2em" }}> {siteDeleteStatus.error} </p>
        </GenericModal>
      );
    } else if (siteDeleteStatus.httpStatus === httpStatus.UNAUTHORIZED_HTTP_STATUS) {
      return (
        <div className="authError">
          <AuthError errorMessage="Unauthorized" />
        </div>
      );
    } else if (siteDeleteStatus.httpStatus === httpStatus.ACCESS_FORBIDDEN_HTTP_STATUS) {
      return (
        <div className="authError">
          <AuthError errorMessage="accessForbidden" />
        </div>
      );
    } else {
      return (
        <GenericModal
          buttonTypeNegative="hollow"
          title={"Failed"}
          LabelPositive="Okay"
          onClickPositive={() => {
            setShowDeleteStatus(false);
          }}
          onClose={() => {
            setShowDeleteStatus(false);
          }}
        >
          <p style={{ paddingTop: "1.2em" }}> {siteDeleteStatus.error} </p>
        </GenericModal>
      );
    }
  };

  // istanbul ignore else
  if (props.siteList.siteList !== null && props.siteList.siteCount > 0) {
    return (
      <>
        <Box className="devices">
          <Paper
            sx={{ width: "100%", mb: 2, backgroundColor: "#272727",
              boxShadow: "unset", color: "#F0F0F0", marginBottom: "0"
            }}
          >
            <TableContainer className="device-table">
              <Table
                sx={{ minWidth: 750, color: "#f0f0f0" }}
                aria-labelledby="tableTitle" size={"small"}
              >
                <EnhancedTableHead
                  order={props.sort.order}
                  orderBy={props.sort.orderBy}
                  onRequestSort={handleRequestSort}
                  rowCount={props.siteList.siteList.length}
                  siteList={props.siteList.siteList}
                  siteListCount={props.siteList.siteList.length}
                />
                <TableBody>
                  {props.siteList.siteList &&
                    props.siteList.siteList.map((row: any, index: any) => {
                      const labelId = `enhanced-table-checkbox-${index}`;
                      return (
                        <StyledTableRow
                          hover role="checkbox" tabIndex={-1} key={row.id}
                          aria-label="site-list-row" sx={{ cursor: "pointer" }}
                        >
                          <StyledTableCell component="th" id={labelId} scope="row" padding="none"
                            onClick={() => handleDeviceDetailClick(row.id, row.siteName) }
                            aria-label="site-name" data-testid="site-name"
                            sx={{ width: '350px', color: "#64C3FF !important" }}
                          >
                            {row.siteName}
                          </StyledTableCell>
                          <StyledTableCell>{row.description}</StyledTableCell>
                          <StyledTableCell style={{width: '280px'}}>
                            {operationPermissions.permissions.can_read_tiq_sites && (
                              <span
                                style={{
                                  color: "#64c3ff",
                                  textDecoration: "none",
                                  paddingLeft: "2px",
                                  marginRight: "15px",
                                }}
                                onClick={() => setOpen(row.id)}
                                data-testid={'site-management-view_' + String(index)}
                              >
                                {DeviceManagementStrings.View}
                              </span>
                            )}
                            {operationPermissions.permissions.can_modify_tiq_sites && (
                              <span
                                style={{
                                  color: "#64c3ff",
                                  textDecoration: "none",
                                  paddingLeft: "2px",
                                  marginRight: "15px",
                                }}
                                onClick={() => { navigate(`/edit-site/${row.id}`) }}
                                data-testid={'site-management-edit_' + String(index)}
                              >
                                {DeviceManagementStrings.Edit}
                              </span>
                            )}
                            {operationPermissions.permissions.can_delete_tiq_sites && (
                              <span
                                style={{
                                  color: "#64c3ff",
                                  textDecoration: "none",
                                  paddingLeft: "2px",
                                }}
                                onClick={() => {
                                  setSelectedSiteId(row.id);
                                  setSelectedSiteName(row.siteName.length > 20 ? `${row.siteName.substring(0, 20)}...` : row.siteName);
                                  setConfirmDeletePopup(true);
                                }}
                                data-testid={'site-management-delete_' + String(index)}
                              >
                                {DeviceManagementStrings.Delete}
                              </span>
                            )}
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <Pagination
              count={props.siteList.siteCount}
              page={props.page}
              setPage={props.setPage}
              rowsPerPage={props.rowsPerPage}
              siblingCount={1}
              boundaryCount={1}
            />
          </Paper>
        </Box>
        { open !== '' &&
          <>
            <ViewSite siteId={open} viewSitePopup={(params?: any) => { setOpen(params) }} />
          </>
        }
        {confirmDeletePopup && (
          <GenericModal width="600px" height="260px"
            buttonTypeNegative="hollow"
            title={`${DeviceManagementStrings.Delete} ${selectedSiteName}`}
            LabelPositive="Confirm"
            LabelNegative="Cancel"
            onClickPositive={() => {
              setConfirmDeletePopup(false);
              setShowDeleteStatus(true);
              props.siteDeleteCampaign(selectedSiteId, selectedSiteName);
            }}
            onClose={() => {
              // showToast();
              setConfirmDeletePopup(false);
            }}
            onClickNegative={() => {
              // showToast();
              setConfirmDeletePopup(false);
            }}
          >
            <p style={{ paddingTop: "1.2em" }}>
              {DeviceManagementStrings.DeleteConfirmation}<br/><br/>
              {DeviceManagementStrings.DeleteConfirmationWarning}
            </p>
          </GenericModal>
        )}
        {
          /* istanbul ignore next */
          showDeleteStatus && handleDeleteCampaignWithFailure()
        }
      </>
    );
  } else if (props.siteList.isLoading) {
    return (
      <div className="CircularprogressAmi" data-testid="loading-spinner">
        <CircularProgress />
      </div>
    );
  } else if (props.siteList.httpStatus === httpStatus.UNAUTHORIZED_HTTP_STATUS) {
    return (
      <div className="authError">
        <AuthError errorMessage="Unauthorized" />
      </div>
    );
  } else if (props.siteList.httpStatus === httpStatus.ACCESS_FORBIDDEN_HTTP_STATUS) {
    return (
      <div className="authError">
        <AuthError errorMessage="accessForbidden" />
      </div>
    );
  } else if (
    props.siteList.siteList !== null &&
    props.siteList.siteList !== undefined &&
    props.siteList.siteList.length === 0
  ) {
    return (
      <div className="authError">
        <AuthError errorMessage="NoDataPresent" />
      </div>
    );
  } else if (
    props.siteList.loadingError?.includes(DeviceManagementStrings.NoAssetsFound) ||
    props.siteList.loadingError?.includes(DeviceManagementStrings.AssetNotFound)
  ) {
    return (
      <div className="authError">
        <AuthError errorMessage="NoDataPresent" />
      </div>
    );
  } else {
    return (
      <div className="authError">
        <AuthError errorMessage="cannotFetch" retry={fetchAllAssetList} />
      </div>
    );
  }
}

interface DispatchToProps {
  fetchSiteList: (PageNumber: number, PageItems: number, searchKey: string, SortParams: string, sortField: string) => void,
  siteDeleteCampaign: (selectedSiteId: string, selectedSiteName: string) => void,
  getOperationPermissions: (sub: string) => void
}
const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchSiteList: (PageNumber: number, PageItems: number, searchKey: string, SortParams: string, sortField: string) => dispatch(fetchSiteList(PageNumber, PageItems, searchKey, SortParams, sortField)),
  siteDeleteCampaign: (selectedSiteId: string, selectedSiteName: string) => dispatch(siteDeleteCampaign(selectedSiteId, selectedSiteName)),
  getOperationPermissions: (sub: string) => dispatch(getOperationPermissions(sub))
});

interface StateToProps {
  siteList: SiteListState;
}
const mapStateToProps = (state: RootState): StateToProps => ({
  siteList: state.siteManagementSiteList
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface ParentProps {
  searchVal: string;
  performAction: () => void;
  rowsPerPage: number;
  page: number;
  sort: Sort;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setSort: React.Dispatch<React.SetStateAction<Sort>>;
}

type AllProps = PropsFromRedux & ParentProps;

export default connector(SiteList);